import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {
    AutomationDomain,
    AutomationFlow,
    AutomationStatus,
} from "crqlar-react-common/dist/models/automation/automation";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../../common/serverTable";
import {AUTOMATION_STATUS_LABEL_KEYS, AutomationStatusElement} from "../../common/uiElements/AutomationStatusElement";
import {DateElement} from "../../common/uiElements/DateElement";
import AutomationCreateDialog from "../../marketing/automations/templates/AutomationCreateDialog";
import {
    RadioFilterOption,
    RadioFilterPanel,
} from "../../layout/rightSidebar/RadioFilterPanel";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";

import {AutomationNameCell, AutomationTriggersCell, getTriggerNodes} from "../../marketing/automations/components/AutomationListCells";
import {useListManualRun} from "../../marketing/automations/hooks/useListManualRun";

const TABLE_ID = "surveys-automations";

type AutomationStatusFilter = AutomationStatus | "ALL";

export default function SurveysAutomationsTab() {
    useTrackScreenVisit('surveys automations');

    const {t} = useTranslation();
    const {appId} = useParams<"appId">();
    const navigate = useNavigate();
    const readOnlyUser = useReadOnly();
    const [selectedStatus, setSelectedStatus] = useState<AutomationStatusFilter>("ALL");
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const {runColumn, runDialog} = useListManualRun(TABLE_ID, "/surveys/automations");

    const openAutomation = useCallback((automation: AutomationFlow) => {
        if (!appId || !automation.id) return;
        navigate(`/surveys/automations/${appId}/${automation.id}`);
    }, [appId, navigate]);

    const columns = useMemo<ColumnDef<AutomationFlow>[]>(() => [
        {
            id: "name",
            header: t("Automation Name"),
            accessorFn: automation => automation.name,
            cell: ({row}) => <AutomationNameCell automation={row.original}/>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "status",
            header: t("Status"),
            accessorFn: automation => automation.status,
            cell: ({row}) => <AutomationStatusElement status={row.original.status}/>,
            size: 150,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "description",
            header: t("Description"),
            accessorFn: automation => automation.description,
            cell: ({row}) => <Box>{row.original.description ?? null}</Box>,
            size: 280,
            meta: {defaultActive: false},
        },
        {
            id: "triggerType",
            header: t("Trigger"),
            accessorFn: automation => getTriggerNodes(automation).map(node => node.type),
            cell: ({row}) => <AutomationTriggersCell automation={row.original}/>,
            size: 150,
            meta: {defaultActive: true},
        },
        runColumn,
        {
            id: "triggerCount",
            header: t("Runs"),
            accessorFn: automation => automation.triggerCount,
            cell: ({row}) => <Box>{row.original.triggerCount ?? null}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "lastTriggeredAt",
            header: t("Last Triggered"),
            accessorFn: automation => automation.lastTriggeredAt,
            cell: ({row}) => <DateElement date={row.original.lastTriggeredAt}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: automation => automation.id,
            cell: ({row}) => <Box>{row.original.id ?? null}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "updateDate",
            header: t("Last updated"),
            accessorFn: automation => automation.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "insertDate",
            header: t("Created"),
            accessorFn: automation => automation.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true},
        },
    ], [t, runColumn]);

    const statusOptions = useMemo<RadioFilterOption<AutomationStatusFilter>[]>(() => [
        {value: "ALL", label: t("All")},
        {value: AutomationStatus.ACTIVE, label: t(AUTOMATION_STATUS_LABEL_KEYS[AutomationStatus.ACTIVE])},
        {value: AutomationStatus.PAUSED, label: t(AUTOMATION_STATUS_LABEL_KEYS[AutomationStatus.PAUSED])},
        {value: AutomationStatus.DRAFT, label: t(AUTOMATION_STATUS_LABEL_KEYS[AutomationStatus.DRAFT])},
    ], [t]);

    const controller = useServerTable<AutomationFlow>({
        tableId: TABLE_ID,
        columns,
        getRowId: automation => automation.id!,
        onRowClick: openAutomation,
        initialSorting: [{id: "id", desc: true}],
        defaultPageSize: 10,
        queryDependencies: [selectedStatus],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await AutomationService.getFlows({
                page: pagination.pageIndex,
                pageSize: pagination.pageSize,
                searchTerm: searchQuery || undefined,
                status: selectedStatus === "ALL" ? undefined : selectedStatus,
                domain: AutomationDomain.SURVEY,
                sortBy: sorting?.field,
                sortDir: sorting?.direction,
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    return (
        <>
            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <ServerTable/>

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <RightSidebarPanelButton
                                    icon={(
                                        <Badge
                                            color="primary"
                                            variant="dot"
                                            invisible={selectedStatus === "ALL"}
                                        >
                                            <FilterAltOutlinedIcon/>
                                        </Badge>
                                    )}
                                    label={t("Status")}
                                    renderPanel={close => (
                                        <RadioFilterPanel<AutomationStatusFilter>
                                            title={t("Status")}
                                            options={statusOptions}
                                            value={selectedStatus}
                                            onChange={setSelectedStatus}
                                            onClose={close}
                                        />
                                    )}
                                />
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <RightSidebarCreateButton
                                    label={t("Create Automation")}
                                    disabled={readOnlyUser}
                                    onClick={() => setCreateDialogOpen(true)}
                                />
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            {runDialog}

            {createDialogOpen && (
                <AutomationCreateDialog
                    open
                    domain={AutomationDomain.SURVEY}
                    onClose={() => setCreateDialogOpen(false)}
                />
            )}
        </>
    );
}
